import {
  findCartProduct,
  findRetailCartProduct
} from 'lib/cart/decorators/cartForm'

import {
  volumeDiscountApplied,
  volumeDiscountUpgradeDays,
  summerPassUpgradeDays
} from 'lib/cart/decorators/cartProduct'

/**
Set the selected location for the appropriate cart product.
If a product is not provided, find the matching retail cart product.
@returns nothing
**/
const selectLocation = (location, { camper, session, product, cartForm }) => {
  const cartProduct = product
    ? findCartProduct({ camper, product, cartForm })
    : findRetailCartProduct({ camper, session, cartForm })

  cartProduct.location = location
}

const selectProduct = (
  product,
  { camper, session, cartForm, selectedType }
) => {
  // find the cart product for this camper
  const cartProduct = findRetailCartProduct({
    camper,
    session,
    cartForm
  })

  // set the product and product type selections
  cartProduct.product = product
  cartProduct.selectedType = selectedType

  if (selectedType == 'unlimited') {
    cartProduct.days = summerPassUpgradeDays(cartProduct)
  } else if (selectedType == 'volume-discount') {
    cartProduct.days = volumeDiscountUpgradeDays(cartProduct)
  } else if (selectedType == 'day-pass') {
    cartProduct.days = 0
  }
}

/**
Upgrades day pass products to unlimited products if needed, and vice versa.
Sets minimum days to 0 and maximum days to unlimited product upgrade days.
@returns nothing
**/
const selectDays = (days, { camper, session, camperSession, cartForm }) => {
  // find the cart product for this camper
  const cartProduct = findRetailCartProduct({
    camper,
    session,
    cartForm
  })

  // don't allow negative days
  cartProduct.days = Math.max(parseInt(days) || 0, 0)

  const { product } = cartProduct
  const volumeDiscountAvailable = product.volumeDiscountCents > 0
  const summerPassAvailable = product.breakEvenDays > 0

  if (volumeDiscountAvailable) {
    if (
      cartProduct.days < volumeDiscountUpgradeDays(cartProduct) &&
      !volumeDiscountApplied(cartProduct)
    ) {
      cartProduct.selectedType = 'day-pass'
    } else if (
      cartProduct.days < summerPassUpgradeDays(cartProduct) ||
      !product.breakEvenDays
    ) {
      cartProduct.selectedType = 'volume-discount'
    } else {
      cartProduct.selectedType = 'unlimited'
      cartProduct.days = summerPassUpgradeDays(cartProduct)
    }
  } else if (summerPassAvailable) {
    if (cartProduct.days < summerPassUpgradeDays(cartProduct)) {
      cartProduct.selectedType = 'day-pass'
    } else {
      cartProduct.selectedType = 'unlimited'
      cartProduct.days = summerPassUpgradeDays(cartProduct)
    }
  } else {
    cartProduct.selectedType = 'day-pass'
  }
}

/**
After fetching products for a given location and camper, select a default if needed.
@returns nothing
**/
const selectDefaultProduct = (
  product,
  { camper, session, camperSession, cartForm }
) => {
  // find the cart product for this camper
  const cartProduct = findRetailCartProduct({
    camper,
    session,
    cartForm
  })

  // returns true if an existing CartProduct needs a default product selection
  const needsDefault = ({ cartProduct, product }) => {
    // select a default product if there is no product selected
    if (!cartProduct.product) {
      return true
    }

    // select the default product if it doesn't match what is in the cart (e.g. price region changed)
    return cartProduct.product.id !== product.id
  }

  if (needsDefault({ cartProduct, product })) {
    if (product) {
      cartProduct.product = product
    } else {
      delete cartProduct.product
    }

    selectDays(cartProduct.days || 0, {
      camper,
      session,
      camperSession,
      cartForm
    })
  }
}

const selectRedeemablePass = (redeemablePass, { camper, cartForm }) => {
  const { product, session } = redeemablePass

  // find the cart product
  const cartProduct = findCartProduct({
    camper,
    product,
    cartForm
  })

  if (cartProduct) {
    // add pass to existing cart product
    cartProduct.redeemablePasses.push(redeemablePass)

    // update cart product days
    const reducer = (days, pass) => days + pass.days
    cartProduct.days = cartProduct.redeemablePasses.reduce(reducer, 0)
  } else {
    // get location from any existing cart product
    const existingCartProduct = cartForm.cartProducts.find(
      cartProduct =>
        cartProduct.location &&
        cartProduct.session.id === session.id &&
        cartProduct.camper.id === camper.id
    )

    const location = existingCartProduct?.location
    const editLocation = location ? false : true
    const camperSession = camper.camperSessions.find(
      cs => cs.session.id === session.id
    )

    // build a new cart product
    const newCartProduct = {
      camper,
      session,
      camperSession,
      product,
      editLocation,
      location,
      redeemablePasses: [redeemablePass],
      days: redeemablePass.days
    }

    cartForm.cartProducts.push(newCartProduct)
  }
}

const removeRedeemablePass = (redeemablePass, { camper, cartForm }) => {
  const { product } = redeemablePass

  // find the cart product
  const cartProduct = findCartProduct({
    camper,
    product,
    cartForm
  })

  // remove the pass from the cart product
  cartProduct.redeemablePasses = cartProduct.redeemablePasses.filter(
    cartProductPass => cartProductPass.id !== redeemablePass.id
  )

  // update cart product days
  const reducer = (days, pass) => days + pass.days
  cartProduct.days = cartProduct.redeemablePasses.reduce(reducer, 0)
}

/**
Toggle date selection for Science Factory locations (reservations by day).
Adds or removes a location day from the cart product's reservation days array.
Automatically updates the days count to match the number of selected dates.
@returns nothing
**/
const selectDate = (locationDay, { camper, session, cartForm }) => {
  // find the cart product for this camper
  const cartProduct = findRetailCartProduct({
    camper,
    session,
    cartForm
  })

  // initialize reservation days array if it doesn't exist
  if (!cartProduct.reservationDays) {
    cartProduct.reservationDays = []
  }

  // check if this date is already selected
  const existingIndex = cartProduct.reservationDays.findIndex(
    rd => rd.locationDayId === locationDay.id
  )

  if (existingIndex >= 0) {
    // remove date if already selected
    cartProduct.reservationDays.splice(existingIndex, 1)
  } else {
    // add date if not selected
    cartProduct.reservationDays.push({
      locationDayId: locationDay.id,
      date: locationDay.date
    })
  }

  // auto-update days count to match selected dates
  cartProduct.days = cartProduct.reservationDays.length
}

export {
  selectProduct,
  selectDays,
  selectDefaultProduct,
  selectLocation,
  selectRedeemablePass,
  removeRedeemablePass,
  selectDate
}
